/**
 * Section-based room patrol registry (headless, pure — no PixiJS).
 *
 * A room's interior is partitioned into rectangular SECTIONS (~5×10 tiles by
 * default). One guard is assigned per section; it scans then roams its own
 * section (see `patrolSection` in guard.ts). The number of sections is derived
 * from room area (capped) and RE-derived from the live guard count whenever the
 * roster changes, so sections enlarge to keep a room fully covered when a guard
 * dies (see `repartitionRoom`, driven by `sim/dispatch.ts`).
 *
 * The `FloorPatrol` registry is held in main.ts beside the `guards` array — it
 * is runtime state, deliberately NOT stored on the static `MapData` grid.
 */

import type { World } from './world';
import { inBounds, isBlocked, roomInteriorBounds, tileAt, type MapData, type Room } from './map';
import { roomAt } from './light';
import { spawnGuard, type Guard } from './guard';
import { blocksVision } from './tiles';

/** Tuning shape consumed here — matches `CONFIG.guard.patrol`. */
export interface PatrolCfg {
  sectionW: number;
  sectionH: number;
  maxSectionsPerRoom: number;
  captainMinRooms: number;
  entranceClearance: number;
  scanHoldTicks: number;
  scanCoverageFrac: number;
  scanTimeoutTicks: number;
  roamDwellTicks: number;
  roamRadius: number;
}

/** A rectangular sub-region of a room's interior owned by one guard. */
export interface Section {
  roomId: number;
  /** Index within the room's current partition (0..n-1). */
  id: number;
  /** Inclusive AABB, clipped to the room interior (walls excluded). */
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  /** A guaranteed-walkable tile inside the section (its patrol/scan anchor). */
  anchorX: number;
  anchorY: number;
}

/** Per-room patrol state: its sections + how many guards belong vs. are present. */
export interface RoomPatrol {
  roomId: number;
  /** The room's display name (Workhall / Armory / …) — for the debug readout. */
  name: string;
  interior: { x0: number; y0: number; x1: number; y1: number };
  sections: Section[];
  /** Area-derived "should be" guard count — constant after build. */
  expectedCount: number;
  /** Live guard count the current partition was built for. */
  assignedCount: number;
  /** A walkable room-centre tile — a captain roam waypoint. */
  centerX: number;
  centerY: number;
}

/** How much of the floor's patrollable area actually has a guard on it, 0..1.
 *  The readable form of `guardCoverage` — used by tests and the debug panel. */
export function patrolCoverage(map: MapData, floor: FloorPatrol): number {
  const spawnRoomId = roomAt(map, map.spawn.x, map.spawn.y)?.id ?? -1;
  const areaOf = (room: Room) => {
    const i = roomInteriorBounds(room);
    return Math.max(1, (i.x1 - i.x0 + 1) * (i.y1 - i.y0 + 1));
  };
  const eligible = map.rooms.filter((r) =>
    r.id !== spawnRoomId && !r.noPatrol
    && !(r.kind !== undefined && NEVER_POSTED.includes(r.kind)));
  const total = eligible.reduce((n, r) => n + areaOf(r), 0);
  if (total === 0) return 0;
  const staffed = eligible
    .filter((r) => floor.rooms.some((rp) => rp.roomId === r.id))
    .reduce((n, r) => n + areaOf(r), 0);
  return staffed / total;
}

/** Whole-floor patrol registry (one floor for now). */
export interface FloorPatrol {
  rooms: RoomPatrol[];
  captainId: number | null;
  /** Round-robin index into `rooms` for the captain's roam route. */
  captainWaypointIdx: number;
  /** Rest-point centre for surplus guards, or null (no green room). */
  greenRoom: { centerX: number; centerY: number } | null;
}

/** Number of ~sectionW×sectionH sections a W×H interior should have, capped. */
export function sectionCountFor(w: number, h: number, cfg: PatrolCfg): number {
  const target = Math.max(1, cfg.sectionW * cfg.sectionH);
  const raw = Math.round((w * h) / target);
  return Math.max(1, Math.min(cfg.maxSectionsPerRoom, raw));
}

/**
 * Partition an inclusive rectangle into EXACTLY `count` gap-free, non-overlapping
 * integer sub-rectangles. Bands run top→bottom; the tile remainder is handed to
 * the leading bands/cells so the pieces tile the rectangle perfectly. Pure
 * geometry (no map) so it's directly unit-testable.
 */
export function partitionRoomBounds(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  count: number,
): Array<{ x0: number; y0: number; x1: number; y1: number }> {
  const W = x1 - x0 + 1;
  const H = y1 - y0 + 1;
  const n = Math.max(1, Math.min(count, W * H));
  let cols = Math.max(1, Math.min(Math.round(Math.sqrt((n * W) / H)), Math.min(n, W)));
  let rows = Math.ceil(n / cols);
  if (rows > H) {
    // Too many bands for the height — cap rows and recompute cols (≤ W since n≤W·H).
    rows = H;
    cols = Math.ceil(n / rows);
  }
  const out: Array<{ x0: number; y0: number; x1: number; y1: number }> = [];
  let yc = y0;
  for (let r = 0; r < rows; r++) {
    const bh = Math.floor(H / rows) + (r < H % rows ? 1 : 0);
    const sc = Math.floor(n / rows) + (r < n % rows ? 1 : 0); // sections in this band
    let xc = x0;
    for (let c = 0; c < sc; c++) {
      const bw = Math.floor(W / sc) + (c < W % sc ? 1 : 0);
      out.push({ x0: xc, y0: yc, x1: xc + bw - 1, y1: yc + bh - 1 });
      xc += bw;
    }
    yc += bh;
  }
  return out;
}

/** Nearest walkable (non-blocking, in-bounds) tile to (x,y) within Chebyshev r. */
export function nearestWalkable(map: MapData, x: number, y: number, r: number): { x: number; y: number } | null {
  for (let rad = 0; rad <= r; rad++) {
    for (let dy = -rad; dy <= rad; dy++) {
      for (let dx = -rad; dx <= rad; dx++) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) !== rad) continue;
        const tx = x + dx;
        const ty = y + dy;
        if (inBounds(map, tx, ty) && !isBlocked(map, tx, ty)) return { x: tx, y: ty };
      }
    }
  }
  return null;
}

/** Build `count` Sections (with resolved walkable anchors) over a room interior. */
export function partitionRoom(
  roomId: number,
  interior: { x0: number; y0: number; x1: number; y1: number },
  count: number,
  map: MapData,
): Section[] {
  const rects = partitionRoomBounds(interior.x0, interior.y0, interior.x1, interior.y1, count);
  return rects.map((rect, id) => {
    const cx = Math.floor((rect.x0 + rect.x1) / 2);
    const cy = Math.floor((rect.y0 + rect.y1) / 2);
    // Search radius spans the section so even a mostly-blocked cell finds a spot.
    const span = Math.max(rect.x1 - rect.x0, rect.y1 - rect.y0) + 1;
    const anchor = nearestWalkable(map, cx, cy, span) ?? { x: cx, y: cy };
    return { roomId, id, x0: rect.x0, y0: rect.y0, x1: rect.x1, y1: rect.y1, anchorX: anchor.x, anchorY: anchor.y };
  });
}

/** Count of a section's tiles a standing guard could actually see (non-opaque)
 *  — the denominator for scan-coverage so cubicle walls can't hang a scan. */
export function countScannableTiles(map: MapData, s: { x0: number; y0: number; x1: number; y1: number }): number {
  let n = 0;
  for (let y = s.y0; y <= s.y1; y++) {
    for (let x = s.x0; x <= s.x1; x++) {
      if (inBounds(map, x, y) && !blocksVision(tileAt(map, x, y))) n++;
    }
  }
  return Math.max(1, n);
}

/** Copy a section onto a guard as its home. `rescan` re-arms the room scan
 *  (used when the guard's ground actually changed). */
export function setGuardSection(g: Guard, roomId: number, s: Section, rescan: boolean): void {
  g.homeRoomId = roomId;
  g.sectionId = s.id;
  g.isCaptain = false;
  g.secX0 = s.x0;
  g.secY0 = s.y0;
  g.secX1 = s.x1;
  g.secY1 = s.y1;
  g.secAnchorX = s.anchorX;
  g.secAnchorY = s.anchorY;
  if (rescan) {
    g.scanning = true;
    g.scanCovered = null;
  }
}

/**
 * Turn a guard into THE floor captain: it owns no section, roams (dispatch sets
 * its `coverTarget`), has night vision, and shows a capital 'G' model. Reused by
 * spawn and by dispatch when the captain is re-elected on death.
 */
export function makeCaptain(g: Guard, roamX: number, roamY: number): void {
  g.isCaptain = true;
  g.homeRoomId = null;
  g.sectionId = null;
  g.secX0 = null;
  g.secY0 = null;
  g.secX1 = null;
  g.secY1 = null;
  g.secAnchorX = null;
  g.secAnchorY = null;
  g.scanning = false;
  g.scanCovered = null;
  // Has goggles; `updateNightVisionGoggles` switches them on when it's in the
  // dark (and blinds it if the lights snap back on in a bright spot).
  g.hasNightVision = true;
  g.nightVision = false;
  g.glyph = 'G';
  g.lastSeenGlyph = 'G';
  g.coverTargetX = roamX;
  g.coverTargetY = roamY;
  g.roamDwellTicks = 0;
  g.path = null;
}

/** True when the guard's stored section AABB differs from `s`. */
function sectionChanged(g: Guard, s: Section): boolean {
  return g.secX0 !== s.x0 || g.secY0 !== s.y0 || g.secX1 !== s.x1 || g.secY1 !== s.y1;
}

/**
 * Greedily assign live guards (id order) to sections by nearest anchor. Re-arms
 * a guard's scan only when its section footprint changed, so unchanged guards
 * don't re-sweep every reshuffle.
 */
export function assignSectionsToGuards(guards: Guard[], sections: Section[], _map: MapData): void {
  const ordered = [...guards].sort((a, b) => a.id - b.id);
  const claimed = new Set<number>();
  for (const g of ordered) {
    let best: Section | null = null;
    let bestD = Infinity;
    for (const s of sections) {
      if (claimed.has(s.id)) continue;
      const d = (s.anchorX - g.x) ** 2 + (s.anchorY - g.y) ** 2;
      if (d < bestD) { bestD = d; best = s; }
    }
    if (!best) break;
    claimed.add(best.id);
    setGuardSection(g, best.roomId, best, sectionChanged(g, best));
  }
}

/** Re-partition a room for its live guards and reassign them across the sections
 *  (sections enlarge/shrink so the whole room stays covered). */
export function repartitionRoom(rp: RoomPatrol, liveInRoom: Guard[], map: MapData): void {
  const count = liveInRoom.length;
  if (count === 0) {
    rp.sections = [];
    rp.assignedCount = 0;
    return;
  }
  rp.sections = partitionRoom(rp.roomId, rp.interior, count, map);
  rp.assignedCount = count;
  assignSectionsToGuards(liveInRoom, rp.sections, map);
}

/** Live, non-captain guards whose home is `roomId`. */
export function guardsInRoom(guards: readonly Guard[], roomId: number): Guard[] {
  return guards.filter((g) => !g.body.dead && !g.isCaptain && g.homeRoomId === roomId);
}

/**
 * Build the floor patrol registry from the map's rooms, excluding the room the
 * player spawns in. Each room gets an area-derived section partition.
 */
/** Rooms nobody is posted to. A guard visits a restroom when they need it or
 *  when something's reported there (see `guard.ts` relief breaks) — standing
 *  post in one is neither realistic nor good to play against. */
const NEVER_POSTED: readonly string[] = ['restroom'];

/**
 * Build the floor's patrol plan, staffed to `coverage` — the fraction of the
 * floor's patrollable AREA that gets a guard posted to it.
 *
 * Rooms are taken largest-first, because if you can only afford three guards
 * you put them in the three biggest spaces, not the stationery cupboard. A room
 * that doesn't make the cut simply isn't in the plan: no sections, no guard, and
 * dispatch won't try to fill it. That's what a cheap building buys you — real
 * blind spots rather than a thinner spread of the same coverage.
 */
export function buildFloorPatrol(map: MapData, cfg: PatrolCfg, coverage = 1): FloorPatrol {
  const spawnRoomId = roomAt(map, map.spawn.x, map.spawn.y)?.id ?? -1;
  const eligible = map.rooms.filter((room) =>
    room.id !== spawnRoomId && !room.noPatrol
    && !(room.kind !== undefined && NEVER_POSTED.includes(room.kind)));
  const areaOf = (room: Room) => {
    const i = roomInteriorBounds(room);
    return Math.max(1, (i.x1 - i.x0 + 1) * (i.y1 - i.y0 + 1));
  };
  const totalArea = eligible.reduce((n, r) => n + areaOf(r), 0);
  const target = totalArea * Math.max(0, Math.min(1, coverage));
  // Biggest first, id-tie-broken so a seed always staffs the same rooms.
  const byArea = [...eligible].sort((a, b) => areaOf(b) - areaOf(a) || a.id - b.id);

  const rooms: RoomPatrol[] = [];
  let covered = 0;
  for (const room of byArea) {
    // Always post at least one guard on a floor that has anywhere to post them,
    // then keep going until the coverage target is met.
    if (rooms.length > 0 && covered >= target) break;
    const interior = roomInteriorBounds(room);
    const w = interior.x1 - interior.x0 + 1;
    const h = interior.y1 - interior.y0 + 1;
    const expectedCount = sectionCountFor(w, h, cfg);
    const sections = partitionRoom(room.id, interior, expectedCount, map);
    const cx = Math.floor((interior.x0 + interior.x1) / 2);
    const cy = Math.floor((interior.y0 + interior.y1) / 2);
    const center = nearestWalkable(map, cx, cy, Math.max(w, h)) ?? { x: cx, y: cy };
    rooms.push({ roomId: room.id, name: room.name, interior, sections, expectedCount, assignedCount: expectedCount, centerX: center.x, centerY: center.y });
    covered += areaOf(room);
  }
  // Rest area: the room named 'Green Room' (a noPatrol room), if present.
  let greenRoom: FloorPatrol['greenRoom'] = null;
  const gr = map.rooms.find((r) => r.name === 'Green Room');
  if (gr) {
    const gi = roomInteriorBounds(gr);
    const gcx = Math.floor((gi.x0 + gi.x1) / 2);
    const gcy = Math.floor((gi.y0 + gi.y1) / 2);
    const c = nearestWalkable(map, gcx, gcy, Math.max(gi.x1 - gi.x0, gi.y1 - gi.y0) + 1) ?? { x: gcx, y: gcy };
    greenRoom = { centerX: c.x, centerY: c.y };
  }
  return { rooms, captainId: null, captainWaypointIdx: 0, greenRoom };
}

/**
 * Spawn one guard per section (inside its section) plus a dedicated floor
 * captain at a floor-central tile. Returns every guard spawned.
 */
export function spawnFloorGuards(world: World, map: MapData, floor: FloorPatrol, cfg: PatrolCfg): Guard[] {
  const out: Guard[] = [];
  // Nobody STARTS on top of the player's way in. The entrance room itself is
  // already noPatrol, but circulation runs right past it, so a corridor section
  // anchored by the door would have a guard staring at the player on frame one.
  // Such a section starts at its farthest corner instead; if the whole section
  // sits in the entrance's lap, it starts empty and dispatch fills it later.
  const clear = cfg.entranceClearance;
  const tooClose = (x: number, y: number) =>
    Math.max(Math.abs(x - map.spawn.x), Math.abs(y - map.spawn.y)) <= clear;
  for (const rp of floor.rooms) {
    for (const s of rp.sections) {
      let sx = s.anchorX;
      let sy = s.anchorY;
      if (tooClose(sx, sy)) {
        const far = farthestTileFrom(map, s, map.spawn);
        if (!far || tooClose(far.x, far.y)) continue; // section is all doorstep
        sx = far.x;
        sy = far.y;
      }
      const g = spawnGuard(world, sx, sy);
      setGuardSection(g, rp.roomId, s, true);
      out.push(g);
    }
  }
  // Man the security desks: the guard whose section contains a station takes it
  // (nearest one otherwise), so the nerve centre is watched rather than wandered.
  for (const st of map.stations) {
    const free = out.filter((g) => g.stationX === null);
    if (free.length === 0) break;
    let best = free[0]!;
    let bestD = Infinity;
    for (const g of free) {
      const inSection = g.secX0 !== null && st.x >= g.secX0 && st.x <= g.secX1!
        && st.y >= g.secY0! && st.y <= g.secY1!;
      const d = (inSection ? 0 : 1000) + Math.max(Math.abs(g.x - st.x), Math.abs(g.y - st.y));
      if (d < bestD) { bestD = d; best = g; }
    }
    best.stationX = st.x;
    best.stationY = st.y;
  }

  // Captain: a dedicated roamer, but only on a floor big enough to spare one.
  // A building that can afford a single guard doesn't have a supervisor.
  if (floor.rooms.length >= cfg.captainMinRooms) {
    let sx = 0;
    let sy = 0;
    for (const rp of floor.rooms) { sx += rp.centerX; sy += rp.centerY; }
    const ax = sx / floor.rooms.length;
    const ay = sy / floor.rooms.length;
    // The captain answers to the entrance clearance like everyone else — it
    // used to be exempt, which put one on the player's doorstep whenever the
    // floor's most central room happened to be near the way in.
    const homes = floor.rooms.filter((rp) => !tooClose(rp.centerX, rp.centerY));
    let home = homes[0] ?? null;
    let bestD = Infinity;
    for (const rp of homes) {
      const d = (rp.centerX - ax) ** 2 + (rp.centerY - ay) ** 2;
      if (d < bestD) { bestD = d; home = rp; }
    }
    if (home) {
      const cap = spawnGuard(world, home.centerX, home.centerY);
      makeCaptain(cap, home.centerX, home.centerY);
      floor.captainId = cap.id;
      out.push(cap);
    }
  }
  return out;
}

/** The walkable tile of `s` farthest from `from`, or null if it has none. */
function farthestTileFrom(
  map: MapData,
  s: Section,
  from: { x: number; y: number },
): { x: number; y: number } | null {
  let best: { x: number; y: number } | null = null;
  let bestD = -1;
  for (let y = s.y0; y <= s.y1; y++) {
    for (let x = s.x0; x <= s.x1; x++) {
      if (isBlocked(map, x, y)) continue;
      const d = Math.max(Math.abs(x - from.x), Math.abs(y - from.y));
      if (d > bestD) { bestD = d; best = { x, y }; }
    }
  }
  return best;
}

/**
 * Debug click-spawn hook: fold a hand-placed guard into the floor plan. A guard
 * dropped on a real (non-spawn) room joins it and re-partitions that room for
 * the new live count (so section count adapts to guard count); dropped elsewhere
 * it stays a legacy wanderer.
 */
export function assignGuardToRoom(floor: FloorPatrol, map: MapData, guards: Guard[], g: Guard, _cfg: PatrolCfg): void {
  const room: Room | null = roomAt(map, g.x, g.y);
  if (!room) return;
  const rp = floor.rooms.find((r) => r.roomId === room.id);
  if (!rp) return; // spawn room (not in floor.rooms) → leave as legacy wanderer
  g.homeRoomId = room.id;
  g.isCaptain = false;
  repartitionRoom(rp, guardsInRoom(guards, room.id), map);
}
